/**
 * Client-side food search: the bundled catalog is loaded as a separate chunk
 * (prefetched when the browser is idle) so search runs locally on every
 * keystroke with no network round trip.
 */
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import type { FoodView } from '../../shared/api';
import { CATALOG_PREFIX, type CatalogFood } from '../../shared/foods';
import { buildIndex, searchIndex } from '../../shared/search';

let catalogPromise: Promise<FoodView[]> | null = null;

function toView(f: CatalogFood): FoodView {
  return {
    ref: CATALOG_PREFIX + f.id,
    name: f.name,
    brand: null,
    per100: f.per100,
    servings: f.servings,
    kind: 'catalog',
    liquid: Boolean(f.liquid),
    barcode: f.barcode ?? null,
    aliases: f.aliases,
  };
}

export function loadCatalog(): Promise<FoodView[]> {
  catalogPromise ??= import('../../shared/catalog').then((m) => m.CATALOG.map(toView));
  return catalogPromise;
}

export function prefetchCatalogWhenIdle(): void {
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
  if (idle) idle(() => void loadCatalog());
  else window.setTimeout(() => void loadCatalog(), 1500);
}

export function useCatalog(): FoodView[] | null {
  const [foods, setFoods] = useState<FoodView[] | null>(null);
  useEffect(() => {
    let alive = true;
    void loadCatalog().then((f) => alive && setFoods(f));
    return () => {
      alive = false;
    };
  }, []);
  return foods;
}

/**
 * Instant search over the user's foods (+ the bundled catalog when FatSecret
 * isn't configured). Foods they've logged before get a boost.
 */
export function useFoodSearch(query: string, custom: FoodView[] | undefined, boostRefs: string[] = [], includeCatalog = true): FoodView[] {
  const catalog = useCatalog();
  const deferred = useDeferredValue(query);
  const index = useMemo(
    () => buildIndex([...(custom ?? []), ...(includeCatalog ? (catalog ?? []) : [])]),
    [custom, catalog, includeCatalog],
  );
  const boost = useMemo(() => new Map(boostRefs.map((ref, i) => [ref, 1.5 - Math.min(1, i / 40)])), [boostRefs]);
  return useMemo(() => (deferred.trim().length < 2 ? [] : searchIndex(index, deferred, { limit: 30, boost })), [index, deferred, boost]);
}
